import { useEffect, useRef, useState } from "react";
import { Crown, DoorOpen, PanelTop, ArrowDown, ArrowUp } from "lucide-react";

export const GROUP_COLORS = [
  { bg: "#e0a94a", fg: "#1a1a1e" }, { bg: "#7fbf94", fg: "#12211a" }, { bg: "#7fa8dd", fg: "#101a26" },
  { bg: "#d9a7d9", fg: "#241a24" }, { bg: "#e79a9a", fg: "#261414" }, { bg: "#a68ee0", fg: "#1a1426" },
];
export const groupColor = (i) => GROUP_COLORS[(i - 1) % GROUP_COLORS.length];

export function GroupSquare({ index, size = 13 }) {
  const c = groupColor(index);
  return <span className="inline-flex items-center justify-center shrink-0 font-bold" style={{ width: size, height: size, borderRadius: 3, background: c.bg, color: c.fg, fontSize: size * 0.62 }}>{index}</span>;
}

export const KIND = {
  attique: { bg: "#2a2035", fg: "#d9a7d9", Icon: Crown },
  etage: { bg: "#241f14", fg: "#e0a94a", Icon: ArrowUp },
  rdc: { bg: "#1c1730", fg: "#a68ee0", Icon: DoorOpen },
  entresol: { bg: "#16241c", fg: "#7fd6ab", Icon: PanelTop },
  sous_sol: { bg: "#141c26", fg: "#7fa8dd", Icon: ArrowDown },
};
const W = 206, PAD = 9;

/** Conserve 200 ms les niveaux retirés (animation de repli) et anime l'apparition des nouveaux. */
function useAnimatedLevels(levels) {
  const [rows, setRows] = useState(() => levels.map((l) => ({ ...l, state: "in" })));
  const prev = useRef(levels);
  useEffect(() => {
    const cur = new Set(levels.map((l) => l.code));
    const gone = prev.current.filter((l) => !cur.has(l.code));
    prev.current = levels;
    const merged = [...levels.map((l) => ({ ...l, state: "in" })), ...gone.map((l) => ({ ...l, state: "out" }))];
    setRows(merged);
    if (gone.length) { const t = setTimeout(() => setRows((r) => r.filter((x) => x.state !== "out")), 200); return () => clearTimeout(t); }
  }, [levels]);
  return rows;
}

/** Croquis de l'immeuble — toit, niveaux animés (200 ms), sol. Partagé entre les étapes 1 et 2 de l'assistant.
 *  `fill` (étape 2, spec iter129) : immeuble pleine hauteur / largeur — toiture dégradée « [BLOC] · TOITURE »,
 *  niveaux répartis équitablement (min 34 px, défilement interne au-delà), note + légende des types. */
export default function BuildingSketch({ levels = [], title, renderRight = null, onLevelClick = null, onLevelEnter = null, selectedCodes = [], width = W, fill = false, blockName = "", lotCount = null, legend = null, headerRight = null, footer = null }) {
  const rows = useAnimatedLevels(levels);
  const top = [...rows].sort((a, b) => b.order - a.order);
  const fs = fill ? 12.5 : 10, ic = fill ? 15 : 11;
  return (
    <div style={{ width, flexShrink: 0, ...(fill ? { display: "flex", flexDirection: "column", minHeight: 0, height: "100%" } : {}) }} data-testid="building-sketch">
      <style>{`@keyframes sketch-in{from{max-height:0;opacity:0}to{max-height:40px;opacity:1}}@keyframes sketch-out{from{max-height:40px;opacity:1}to{max-height:0;opacity:0}}`}</style>
      <div className="flex items-center" style={{ fontSize: 10.5, color: "#8b8b93", marginBottom: 6, minHeight: 22 }} data-testid="building-sketch-count"><span style={{ flex: 1 }}>{title || `APERÇU · ${levels.length} NIVEAU${levels.length > 1 ? "X" : ""}`}</span>{headerRight}</div>
      <div style={{ background: "#101013", border: "0.5px solid #222226", borderRadius: 9, padding: `10px ${PAD}px`, ...(fill ? { flex: 1, minHeight: 0, display: "flex", flexDirection: "column" } : {}) }}>
        {fill ? (
          <div style={{ margin: `0 -${PAD}px 2px` }} data-testid="building-sketch-roof">
            <div style={{ width: 0, height: 0, margin: "0 auto", borderLeft: "34px solid transparent", borderRight: "34px solid transparent", borderBottom: "22px solid #3a3568" }} aria-hidden="true" />
            <div className="text-center" style={{ background: "linear-gradient(90deg, #3a3568, #5B5FC7)", color: "#c9c7f0", fontSize: 9.5, letterSpacing: ".08em", padding: "5px 8px", borderRadius: "8px 8px 0 0" }} data-testid="building-sketch-roof-label">{(blockName || "BLOC").toString().toUpperCase()} · TOITURE</div>
          </div>
        ) : (
          <div style={{ margin: `0 -${PAD}px 2px`, height: 0, borderLeft: `${width / 2}px solid transparent`, borderRight: `${width / 2}px solid transparent`, borderBottom: "15px solid #2a2035", width }} aria-hidden="true" data-testid="building-sketch-roof" />
        )}
        <div className="sketch-scroll" style={{ display: "flex", flexDirection: "column", gap: 2, ...(fill ? { flex: 1, minHeight: 0, overflowY: top.length > 12 ? "auto" : "hidden" } : { maxHeight: 330, overflowY: "auto" }) }} data-testid="building-sketch-levels">
          {top.map((l, i) => {
            const k = KIND[l.kind] || KIND.etage;
            const last = i === top.length - 1;
            const active = selectedCodes.includes(l.code);
            return (
              <div key={l.code} onClick={onLevelClick && l.state === "in" ? () => onLevelClick(l) : undefined} onMouseEnter={onLevelEnter ? () => onLevelEnter(l) : undefined}
                className="flex items-center select-none" style={{ gap: fill ? 10 : 7, padding: fill ? "8px 12px" : "5px 9px", fontSize: fs, fontWeight: active ? 500 : 400, background: active ? "#2A9A7A" : k.bg, color: active ? "#fff" : k.fg, borderRadius: last ? "0 0 5px 5px" : 0, cursor: onLevelClick ? "pointer" : "default", overflow: "hidden",
                  ...(fill ? { flex: "1 1 0", minHeight: 34, opacity: l.state === "out" ? 0 : 1, transition: "opacity 200ms" } : { animation: `${l.state === "out" ? "sketch-out" : "sketch-in"} 200ms ease-out both` }) }}
                data-testid={`sketch-level-${l.code}`} data-selected={active || undefined}>
                <k.Icon size={ic} style={{ color: active ? "#fff" : k.fg, flexShrink: 0 }} />
                {l.kind === "etage" && l.group_index && <GroupSquare index={l.group_index} size={fill ? 16 : 13} />}
                <span className="truncate" style={{ flex: 1 }}>{l.label}</span>
                {renderRight?.(l)}
              </div>
            );
          })}
        </div>
        <div style={{ margin: `6px -${PAD}px 0`, height: 3, background: "#33333a", borderRadius: 2 }} aria-hidden="true" data-testid="building-sketch-ground" />
      </div>
      {footer}
      <div className="text-center" style={{ fontSize: 9, color: "#5a5a63", marginTop: 6 }} data-testid="building-sketch-note">
        Le croquis se construit au fil de votre saisie{fill && ` · ${levels.length} niveau${levels.length > 1 ? "x" : ""}`}{fill && lotCount != null && ` · ${lotCount} lot${lotCount > 1 ? "s" : ""}`}
      </div>
      {fill && legend && legend.length > 0 && (
        <div className="flex items-center justify-center flex-wrap" style={{ gap: 10, marginTop: 6 }} data-testid="building-sketch-legend">
          {legend.map((x) => <span key={x.label} className="inline-flex items-center" style={{ gap: 5, fontSize: 9.5, color: "#8b8b93" }}><span style={{ width: 9, height: 9, borderRadius: 2, background: x.color, display: "inline-block" }} />{x.label}</span>)}
        </div>
      )}
    </div>
  );
}
